import { createAsyncThunk, createSelector, createSlice, } from "@reduxjs/toolkit";
import { getEvolutions, } from "../../api/evolutions/getEvolutions";
import { includedAfterRefetch, } from "../../evolutions/picker";
export const evolutionsInitialState = {
    status: "idle",
    evolutions: [],
    expiredEvolutions: [],
    categories: {},
    includedEvolutions: [],
    mustIncludeEvolutions: [],
    activePickerSelection: { type: "state", state: "available" },
};
/** Where the half-filled entries the backend sometimes sends get reported. */
let contractReporter;
export const setEvolutionsContractReporter = (reporter) => {
    contractReporter = reporter;
};
export const fetchEvolutions = createAsyncThunk("evolutions", async () => {
    return await getEvolutions(contractReporter);
});
const evolutionsSlice = createSlice({
    name: "evolutions",
    initialState: evolutionsInitialState,
    reducers: {
        /** Marks the catalogue stale so the next screen refetches it. */
        invalidateEvolutions: (state) => {
            state.status = "idle";
        },
        setIncludedEvolutions: (state, action) => {
            state.includedEvolutions = action.payload;
        },
        setMustIncludeEvolutions: (state, action) => {
            state.mustIncludeEvolutions = action.payload;
        },
        setIncludedEvolutionsByState: (state, action) => {
            state.includedEvolutions = state.evolutions
                .filter((evo) => evo.state === action.payload && !evo.excludedByDefault)
                .map((evo) => evo.id);
        },
        setMustIncludeEvolutionsByState: (state, action) => {
            state.mustIncludeEvolutions = state.evolutions
                .filter((evo) => evo.state === action.payload)
                .map((evo) => evo.id);
        },
        setActivePickerSelection: (state, action) => {
            state.activePickerSelection = action.payload;
        },
        /** A saved state change, applied without refetching the catalogue. */
        setEvolutionStates: (state, action) => {
            const next = new Map(action.payload.map((u) => [u.id, u.state]));
            for (const evo of state.evolutions) {
                const updated = next.get(evo.id);
                if (updated)
                    evo.state = updated;
            }
        },
        /** What the picker's Apply commits — both lists in one action. */
        applyEvolutionSelection: (state, action) => {
            state.includedEvolutions = action.payload.includedEvolutions;
            state.mustIncludeEvolutions = action.payload.mustIncludeEvolutions;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchEvolutions.pending, (state) => {
            state.status = "pending";
        })
            .addCase(fetchEvolutions.fulfilled, (state, action) => {
            // Read before the catalogue is replaced below.
            const included = includedAfterRefetch({
                included: state.includedEvolutions,
                previous: state.evolutions,
                next: action.payload,
            });
            state.status = "fulfilled";
            state.evolutions = action.payload.evolutions;
            state.expiredEvolutions = action.payload.expiredEvolutions;
            state.categories = Object.fromEntries(action.payload.categories.map((c) => [c.id, c]));
            // First load: everything the user can start right now, as on the web
            // hub. A refetch keeps a selection the user made.
            state.includedEvolutions = included;
        })
            .addCase(fetchEvolutions.rejected, (state) => {
            state.status = "rejected";
        });
    },
});
export const evolutionsSelector = (state) => state.evolutionsReducer;
export const includedEvolutionsSelector = (state) => state.evolutionsReducer.includedEvolutions;
export const mustIncludeEvolutionsSelector = (state) => state.evolutionsReducer.mustIncludeEvolutions;
/**
 * The catalogue as one lookup. Chain steps arrive as bare evolution ids, so
 * every step is a lookup against this. Expired evolutions are folded in and
 * flagged — a completed chain can reference one that has since dropped out
 * of the live list.
 */
export const evolutionsByIdSelector = createSelector((state) => state.evolutionsReducer.evolutions, (state) => state.evolutionsReducer.expiredEvolutions, (evolutions, expiredEvolutions) => {
    const byId = {};
    for (const evo of expiredEvolutions)
        byId[evo.id] = { ...evo, expired: true };
    for (const evo of evolutions)
        byId[evo.id] = evo;
    return byId;
});
export const activePickerSelectionSelector = (state) => state.evolutionsReducer.activePickerSelection;
/** Highest priority first — the order the picker's category list uses. */
export const evolutionCategoriesOrderedSelector = createSelector((state) => state.evolutionsReducer.categories, (categoriesById) => Object.values(categoriesById).sort((a, b) => b.priority - a.priority));
export const { invalidateEvolutions, setIncludedEvolutions, setMustIncludeEvolutions, setIncludedEvolutionsByState, setMustIncludeEvolutionsByState, setActivePickerSelection, applyEvolutionSelection, setEvolutionStates, } = evolutionsSlice.actions;
/**
 * The reducer under its own name too, for a host that composes extra state
 * on top of the catalogue (web adds the overview sort and the user-state
 * update) and hands every other action here.
 */
export const evolutionsReducer = evolutionsSlice.reducer;
export default evolutionsSlice.reducer;
//# sourceMappingURL=evolutionsSlice.js.map